import { jsPDF } from "jspdf";
import {
  getWorkSubmittedAt,
  getWorkSubmitterId,
} from "../../../lib/workSubmissionMeta";

/**
 * Helper function to load image and convert to base64
 */
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      try {
        const base64 = canvas.toDataURL("image/jpeg", 0.8);
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Generates a Work Completion Report PDF for a customer
 * @param {Object} customer - Customer object
 * @param {Object} survey - Survey data
 * @param {Object} civilWork - Civil work data
 * @param {Object} technicalWork - Technical work data
 * @param {Object|null} commissioningWork - Commissioning work (testing fields shown on report)
 * @param {Object} region - Region object
 * @param {Object} cluster - Cluster object
 * @param {Array} users - Users list for submitter names
 * @returns {Promise<void>}
 */
export const generateWorkCompletionReport = async (
  customer,
  survey,
  civilWork,
  technicalWork,
  commissioningWork = null,
  region = null,
  cluster = null,
  users = [],
) => {
  try {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    const colors = {
      primary: [19, 105, 63],
      secondary: [19, 105, 63],
      text: [44, 62, 80],
      lightGray: [236, 240, 241],
      border: [189, 195, 199],
    };

    // Helper function to add new page if needed
    const checkPageBreak = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    const addSectionTitle = (title, number) => {
      const sectionTopGap = 5;
      if (yPos > margin) {
        checkPageBreak(20 + sectionTopGap);
        yPos += sectionTopGap;
      } else {
        checkPageBreak(20);
      }

      pdf.setFillColor(...colors.lightGray);
      pdf.roundedRect(
        margin - 2,
        yPos - 5,
        pageWidth - margin * 2 + 4,
        12,
        2,
        2,
        "F",
      );

      pdf.setFillColor(...colors.primary);
      pdf.setDrawColor(...colors.primary);
      pdf.circle(margin + 5, yPos + 1, 4, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.text(number.toString(), margin + 5, yPos + 2.5, { align: "center" });

      pdf.setFontSize(13);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...colors.text);
      pdf.text(title, margin + 12, yPos + 2);

      yPos += 12;
    };

    const formatValue = (value) => {
      if (value === null || value === undefined || value === "") return "N/A";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return String(value);
    };

    const feetToMeters = (feetValue) => {
      const feet = Number.parseFloat(feetValue);
      if (!Number.isFinite(feet)) return "";
      return (feet * 0.3048).toFixed(2);
    };

    const formatCableLength = (feetValue, metersValue) => {
      const meters =
        String(metersValue ?? "").trim() || feetToMeters(feetValue) || "";
      if (!meters) return "";
      return `${meters} m`;
    };

    const addKeyValueTable = (rows) => {
      if (!rows || rows.length === 0) return;

      const tableX = margin;
      const tableWidth = pageWidth - margin * 2;
      const labelColWidth = Math.min(65, Math.max(52, tableWidth * 0.38));
      const valueColWidth = tableWidth - labelColWidth;
      const lineHeight = 4.5;
      const cellPaddingX = 2;
      const cellPaddingY = 3.5;

      checkPageBreak(12);
      pdf.setFillColor(...colors.primary);
      pdf.setDrawColor(...colors.border);
      pdf.setLineWidth(0.2);
      pdf.rect(tableX, yPos, tableWidth, 8, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text("Field", tableX + cellPaddingX, yPos + 5.2);
      pdf.text("Value", tableX + labelColWidth + cellPaddingX, yPos + 5.2);
      yPos += 8;

      pdf.setFontSize(8.5);
      for (let i = 0; i < rows.length; i++) {
        const [rawLabel, rawValue] = rows[i];
        const label = formatValue(rawLabel);
        const value = formatValue(rawValue);

        const labelLines = pdf.splitTextToSize(label, labelColWidth - cellPaddingX * 2);
        const valueLines = pdf.splitTextToSize(value, valueColWidth - cellPaddingX * 2);
        const contentLines = Math.max(labelLines.length, valueLines.length);
        const rowHeight = cellPaddingY + contentLines * lineHeight + 1.5;

        checkPageBreak(rowHeight + 2);

        pdf.setFillColor(...colors.lightGray);
        pdf.setDrawColor(...colors.border);
        pdf.rect(tableX, yPos, labelColWidth, rowHeight, "FD");

        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(...colors.border);
        pdf.rect(tableX + labelColWidth, yPos, valueColWidth, rowHeight, "FD");

        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text(labelLines, tableX + cellPaddingX, yPos + cellPaddingY + 0.5);

        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "normal");
        pdf.text(valueLines, tableX + labelColWidth + cellPaddingX, yPos + cellPaddingY + 0.5);

        yPos += rowHeight;
      }

      yPos += 4;
    };

    const addLabeledImageGrid = async (items, title, maxPerRow = 2) => {
      if (!items || items.length === 0) return;

      checkPageBreak(50);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...colors.secondary);
      pdf.text(title, margin, yPos);
      yPos += 6;

      const imageWidth = (pageWidth - margin * 2 - (maxPerRow - 1) * 5) / maxPerRow;
      const imageHeight = imageWidth * 0.75;
      const captionTopGap = 2;

      for (let i = 0; i < items.length; i++) {
        if (i > 0 && i % maxPerRow === 0) {
          yPos += imageHeight + 12;
          checkPageBreak(imageHeight + 18);
        }

        const col = i % maxPerRow;
        const x = margin + col * (imageWidth + 5);
        const currentY = yPos;

        const label = items[i]?.label ? String(items[i].label) : "";
        const url = items[i]?.url;

        try {
          const base64Image = await loadImageAsBase64(url);
          pdf.setDrawColor(...colors.border);
          pdf.setLineWidth(0.5);
          pdf.rect(x, currentY, imageWidth, imageHeight);
          pdf.addImage(base64Image, "JPEG", x + 1, currentY + 1, imageWidth - 2, imageHeight - 2);
        } catch (error) {
          console.error(`Error loading image ${url}:`, error);
          pdf.setFillColor(...colors.lightGray);
          pdf.rect(x, currentY, imageWidth, imageHeight, "F");
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text("Image not available", x + imageWidth / 2, currentY + imageHeight / 2, {
            align: "center",
          });
        }

        if (label) {
          pdf.setFontSize(8);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(...colors.text);
          const captionLines = pdf.splitTextToSize(label, imageWidth);
          const captionY = currentY + imageHeight + captionTopGap + 2;
          pdf.text(captionLines, x, captionY);
        }
      }

      yPos += imageHeight + 12;
    };

    // Header with logo
    const logoPath = "/assets/images/brand.png";
    const logoBase64 = await loadImageAsBase64(logoPath);
    const logoWidth = 26;
    const logoHeight = 26;
    pdf.addImage(logoBase64, "PNG", margin, 4, logoWidth, logoHeight);

    pdf.setTextColor(...colors.text);
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text("WORK COMPLETION REPORT", pageWidth / 2, 15, { align: "center" });

    pdf.setDrawColor(...colors.primary);
    pdf.setLineWidth(0.5);
    pdf.line(margin, 30, pageWidth - margin, 30);

    yPos = 40;

    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(9);
    const reportDate = new Date().toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    pdf.text(`Report Generated: ${reportDate}`, pageWidth - margin, yPos, {
      align: "right",
    });
    yPos += 8;

    const panels = technicalWork?.panels || {};
    const wiringInverter = technicalWork?.wiring_inverter || {};
    const wiring = technicalWork?.wiring || {};
    const installationPhotos = technicalWork?.installation_photos || {};
    const earthing = technicalWork?.earthing || {};
    const verification = commissioningWork?.verification || {};
    const userById = new Map((Array.isArray(users) ? users : []).map((u) => [u.id, u]));
    const verificationLabels = [
      ["cable_routing", "Cable routing"],
      ["lightning_arrester_checked", "Lightning arrester checked"],
      ["civil_structure", "Civil structure"],
      ["earthing_pits", "Earthing pits"],
      ["inverter_functionality_confirmed", "Inverter functionality confirmed"],
      ["safety_systems_operation", "Safety systems operation"],
    ];
    const getSubmissionMeta = (row, phase) => {
      if (!row) return { submittedAt: "N/A", submitterLabel: "N/A" };
      const submittedAt = getWorkSubmittedAt(row, phase);
      const submitterId = getWorkSubmitterId(row);
      const submitter = submitterId ? userById.get(submitterId) : null;
      const submitterLabel = submitter?.name?.trim() || "N/A";
      return {
        submittedAt: submittedAt
          ? new Date(submittedAt).toLocaleString("en-IN")
          : "N/A",
        submitterLabel,
      };
    };
    const technicalSubmission = getSubmissionMeta(technicalWork, "technical");
    const commissioningSubmission = getSubmissionMeta(
      commissioningWork,
      "commissioning",
    );

    // Mark intentionally unused parameters; function signature is shared.
    void civilWork;

    addSectionTitle("CUSTOMER INFORMATION", "1");
    addKeyValueTable([
      ["Customer Name", customer?.name],
      ["CIN (Customer ID)", customer?.cin],
      ["USN (Service Number)", customer?.usn],
      ["Phone Number", customer?.phone_number],
      ["Aadhaar Number", customer?.aadhaar_number],
      ["Date of Birth", customer?.dob ? new Date(customer.dob).toLocaleDateString("en-IN") : "N/A"],
      ["Address", customer?.address],
      ["Village", customer?.village],
      ["District", customer?.district],
      ["State", customer?.state],
      ["Pincode", customer?.pincode],
      ["Region", region?.name],
      ["Cluster", cluster?.name],
      [
        "Coordinates",
        customer?.latitude && customer?.longitude ? `${customer.latitude}, ${customer.longitude}` : "N/A",
      ],
    ]);

    let sectionNo = 2;
    addSectionTitle("PANEL & INVERTER SERIAL NUMBERS", String(sectionNo++));
    const serialRows = [1, 2, 3, 4].map((panelId) => {
      const commissioningSerial = commissioningWork?.[`panel_number_${panelId}`];
      const panel = panels[panelId] || {};
      const technicalSerial =
        panel.serialNumber ||
        panel.serial_number ||
        panel.panelSerialNumber ||
        panel.panel_serial_number;
      return [`Panel ${panelId} serial number`, commissioningSerial || technicalSerial || "N/A"];
    });
    const commissioningInverter = commissioningWork?.inverter_number;
    serialRows.push([
      "Inverter serial number",
      commissioningInverter ||
        wiringInverter.inverterSerialNumber ||
        wiringInverter.inverter_serial_number ||
        "N/A",
    ]);
    const dcMeters = String(wiring.dcMeters ?? "").trim();
    serialRows.push(
      [
        "DC Cable Length (4 sqmm) - Red",
        formatCableLength(wiring.dcRedFeet, null),
      ],
      [
        "DC Cable Length (4 sqmm) - Black",
        formatCableLength(wiring.dcBlackFeet, null),
      ],
      ["DC Cable Length (4 sqmm) - Total", dcMeters ? `${dcMeters} m` : ""],
      [
        "AC Cable Length (2 Core, 4 sqmm)",
        formatCableLength(wiring.acFeet, wiring.acMeters),
      ],
      [
        "Earthing Cable Length (16 sqmm)",
        formatCableLength(wiring.earthingFeet, wiring.earthingMeters),
      ],
    );
    addKeyValueTable(serialRows);

    addSectionTitle("SUBMISSION DETAILS", String(sectionNo++));
    addKeyValueTable([
      ["Technical submitted at", technicalSubmission.submittedAt],
      ["Technical submitted by", technicalSubmission.submitterLabel],
      ["Commissioning submitted at", commissioningSubmission.submittedAt],
      ["Commissioning submitted by", commissioningSubmission.submitterLabel],
    ]);

    addSectionTitle("WORK EVIDENCE PHOTOS", String(sectionNo++));
    const evidencePhotoItems = [];

    // Survey site photo(s)
    if (survey?.house_front_view_photos?.length > 0) {
      survey.house_front_view_photos.forEach((url, idx) => {
        if (url) evidencePhotoItems.push({ url, label: `House Front View Photo ${idx + 1}` });
      });
    }

    // Technical / commissioning evidence photos
    evidencePhotoItems.push(
      { url: earthing.photo1 || earthing.photo2, label: "Earth Pit" },
      { url: commissioningWork?.voltage_meter_photo, label: "Voltage Picture" },
      { url: commissioningWork?.main_meter_serial_photo, label: "Main meter serial photo" },
      { url: commissioningWork?.customer_photo_with_panels_side, label: "Customer with panels (side)" },
      { url: commissioningWork?.customer_photo_with_panels_front, label: "Customer with panels (front)" },
      { url: commissioningWork?.customer_photo_with_inverter, label: "Customer with inverter" },
    );

    const evidencePhotos = evidencePhotoItems
      .filter((item) => !!item?.url)
      .map((item, idx) => ({
        ...item,
        label: `${idx + 1}. ${item.label}`,
      }));

    if (evidencePhotos.length > 0) {
      await addLabeledImageGrid(evidencePhotos, "Evidence Photos", 2);
    } else {
      addKeyValueTable([["Evidence photos", "No photos available"]]);
    }

    addSectionTitle("COMMISSIONING CHECKLIST", String(sectionNo++));
    addKeyValueTable(
      verificationLabels.map(([key, label]) => [
        label,
        verification?.[key] ? "Yes" : "No",
      ]),
    );

    yPos += 10;

    checkPageBreak(30);
    pdf.setFillColor(...colors.lightGray);
    pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 25, 2, 2, "F");

    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...colors.primary);
    pdf.text("COMPLETION STATEMENT", pageWidth / 2, yPos + 6, { align: "center" });
    yPos += 10;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...colors.text);
    const completionText =
      "This report contains customer details, technical serial numbers, photo evidence, and commissioning checklist status for completed installation work.";
    const completionLines = pdf.splitTextToSize(
      completionText,
      pageWidth - margin * 2 - 6,
    );
    pdf.text(completionLines, margin + 3, yPos);
    yPos += completionLines.length * 5 + 8;

    const signatureTopGap = 6;
    checkPageBreak(signatureTopGap + 6);
    yPos += signatureTopGap;
    pdf.setDrawColor(...colors.border);
    pdf.line(margin, yPos, margin + 60, yPos);
    pdf.setFontSize(8);
    pdf.setTextColor(...colors.secondary);
    pdf.text("Authorized Signatory", margin, yPos - 2);

    pdf.line(pageWidth - margin - 60, yPos, pageWidth - margin, yPos);
    pdf.text("Date", pageWidth - margin - 30, yPos - 2, { align: "center" });

    const customerIdentifier =
      customer?.cin || customer?.usn || customer?.id?.slice(0, 8) || "customer";
    const timestamp = new Date().toISOString().split("T")[0];
    pdf.save(`Work_Completion_Report_${customerIdentifier}_${timestamp}.pdf`);
  } catch (error) {
    console.error("Error generating work completion report:", error);
    throw error;
  }
};
